import { useTranslation } from "react-i18next";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Tag } from "components/designV2/tag/tag";
import { TextArea } from "components/designV2/textarea/textarea";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { PaymentMethod } from "model/Gig";
import type {
  TeamPublicDetailsEditActions,
  TeamPublicDetailsEditViewState,
} from "./teamPublicDetailsModal.contract";
import {
  DescriptionInputWrap,
  EditBottomGrid,
  EditDescriptionRow,
  EditFieldLabel,
  EditSectionLayout,
  EditTopGrid,
  IconActionButton,
  RangeEditRow,
  RoleHintText,
  SkillsAddButtonRow,
  SkillsInputRow,
  SkillsTags,
  WorkerStepperRow,
  WorkerStepperValue,
} from "./teamPublicDetailsModal.styled";

const MIN_WORKERS = 1;
const MAX_WORKERS = 999;

interface TeamPublicDetailsModalEditSectionProps {
  readonly isPublicShift: boolean;
  readonly editViewState: TeamPublicDetailsEditViewState;
  readonly editActions: TeamPublicDetailsEditActions;
}

export const TeamPublicDetailsModalEditSection: React.FC<
  TeamPublicDetailsModalEditSectionProps
> = (props) => {
  const { t } = useTranslation();
  const parsedWorkerCount = Number(props.editViewState.editAmountOfWorkers);
  const workerCount = Number.isFinite(parsedWorkerCount)
    ? parsedWorkerCount
    : MIN_WORKERS;

  return (
    <EditSectionLayout>
      <EditTopGrid>
        <div>
          <EditFieldLabel>{`${t("CreateShift.RoleWanted")}*`}</EditFieldLabel>
          <InputField
            value={props.editViewState.editRole}
            onChange={props.editActions.setEditRole}
            placeholder={t("CreateShift.RoleWantedPlaceholder")}
            maxLength={24}
            fullWidth
          />
          <RoleHintText>{`${props.editViewState.editRole.length}/24`}</RoleHintText>
        </div>
        <div>
          <EditFieldLabel>{t("CreateShift.WorkersNeeded")}</EditFieldLabel>
          <WorkerStepperRow>
            <IconActionButton
              type="button"
              aria-label={t("General.Decrease")}
              disabled={workerCount <= MIN_WORKERS}
              onClick={() => {
                if (workerCount > MIN_WORKERS) {
                  props.editActions.setEditAmountOfWorkers(
                    String(workerCount - 1),
                  );
                }
              }}
            >
              <CustomIcon
                name="minus"
                color={ColorV2.Blue4}
                backgroundColor={ColorV2.Blue6}
                padding="6px"
              />
            </IconActionButton>
            <WorkerStepperValue>{workerCount}</WorkerStepperValue>
            <IconActionButton
              type="button"
              aria-label={t("General.Increase")}
              disabled={workerCount >= MAX_WORKERS}
              onClick={() => {
                if (workerCount < MAX_WORKERS) {
                  props.editActions.setEditAmountOfWorkers(
                    String(workerCount + 1),
                  );
                }
              }}
            >
              <CustomIcon
                name="plus_old"
                color={ColorV2.White}
                backgroundColor={ColorV2.Blue1}
                padding="6px"
              />
            </IconActionButton>
          </WorkerStepperRow>
        </div>
      </EditTopGrid>
      <EditDescriptionRow>
        <EditFieldLabel>{t("CreateShift.ShiftDescription")}</EditFieldLabel>
        <DescriptionInputWrap>
          <TextArea
            value={props.editViewState.editDescription}
            onChange={props.editActions.setEditDescription}
            placeholder={t("CreateShift.ShiftDescriptionPlaceholder")}
            maxHeight={null}
            minHeight={56}
            rows={1}
          />
        </DescriptionInputWrap>
      </EditDescriptionRow>
      {props.isPublicShift && (
        <EditDescriptionRow>
          <EditFieldLabel>{t("CreateShift.ExperienceSkills")}</EditFieldLabel>
          <SkillsInputRow>
            <InputField
              value={props.editViewState.editSkillInput}
              onChange={props.editActions.setEditSkillInput}
              placeholder={t("CreateShift.ExperienceSkillsPlaceholder")}
              size="compact"
              fullWidth
              maxLength={24}
            />
            <SkillsAddButtonRow>
              <SecondaryButton
                size="small"
                label={t("General.Add")}
                onClick={props.editActions.handleAddSkill}
                disabled={!props.editViewState.editSkillInput.trim()}
              />
            </SkillsAddButtonRow>
          </SkillsInputRow>
          {props.editViewState.editSkills.length > 0 && (
            <SkillsTags>
              {props.editViewState.editSkills.map((skillItem) => (
                <Tag
                  key={
                    skillItem.type === "persisted"
                      ? String(skillItem.id)
                      : skillItem.localId
                  }
                  text={skillItem.skill}
                  backgroundColor={ColorV2.Blue6}
                  textColor={ColorV2.Blue4}
                  onClear={() => props.editActions.handleRemoveSkill(skillItem)}
                />
              ))}
            </SkillsTags>
          )}
        </EditDescriptionRow>
      )}
      {props.isPublicShift && (
        <EditBottomGrid>
          <div>
            <EditFieldLabel>{t("Schedule.GigPaymentType")}</EditFieldLabel>
            <Dropdown
              options={[
                {
                  value: PaymentMethod.NONE,
                  label: t("GigsCompany.OwnSalaryProcessing"),
                },
                {
                  value: PaymentMethod.SIMFLEXITY_PAY,
                  label: t("SimflexityPay.SimflexityPay"),
                },
              ]}
              value={props.editViewState.editPaymentType}
              onChange={props.editActions.setEditPaymentType}
              fullWidth
            />
          </div>
          <div>
            <EditFieldLabel>{t("Schedule.GigSalarySpan")}</EditFieldLabel>
            <RangeEditRow>
              <InputField
                value={props.editViewState.editMinRate}
                onChange={props.editActions.setEditMinRate}
                type="number"
              />
              <span>-</span>
              <InputField
                value={props.editViewState.editMaxRate}
                onChange={props.editActions.setEditMaxRate}
                type="number"
              />
            </RangeEditRow>
          </div>
        </EditBottomGrid>
      )}
    </EditSectionLayout>
  );
};
